import React, { useState } from 'react';
import { FileText, Edit2, X } from 'lucide-react';
import { API } from '../../App';

/**
 * ProjectBriefPanel — surfaces the project's "instructions" context.
 * Read view (Task 3.1) + edit modal (Task 3.2) here in one component.
 *
 * Save flow (destructive step last so a partial-failure recovery is trivial):
 *   1. POST /api/contexts to create the new context with the textarea content.
 *   2. POST /api/projects/{id}/contexts to attach the new context as instructions.
 *      (Server uses ON CONFLICT, so the role gets swapped atomically — but we
 *      can't rely on cascade-delete-old since project_contexts is keyed by
 *      (project_id, context_id), not by role.)
 *   3. DELETE the prior instructions context's project attachment.
 *   4. Close modal + call onSaved so parent refetches.
 */
export default function ProjectBriefPanel({ projectId, contexts = [], onEdit, onSaved }) {
  const brief = (contexts || []).find((c) => c.role === 'instructions');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const openEdit = () => {
    setDraft(brief?.content || '');
    setError(null);
    setEditing(true);
    if (onEdit) onEdit();
  };

  const closeEdit = () => {
    setEditing(false);
    setDraft('');
    setError(null);
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const r = await API.post('/api/contexts', {
        content: draft,
        title: 'Project Brief',
        type: 'document',
      });
      const newId = r?.data?.id;
      if (newId) {
        await API.post(`/api/projects/${projectId}/contexts`, {
          context_id: newId,
          role: 'instructions',
        });
      }
      if (brief?.context_id && brief.context_id !== newId) {
        try {
          await API.delete(`/api/projects/${projectId}/contexts/${brief.context_id}`);
        } catch (cleanupErr) {
          // The new brief already attached fine — leaving the old attachment
          // is a recoverable state (FilesTree will show two instructions
          // contexts but the new one is what fray runs read first).
          // eslint-disable-next-line no-console
          console.error('Old brief attachment cleanup failed (recoverable):', cleanupErr);
        }
      }
      setEditing(false);
      setDraft('');
      if (onSaved) onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save brief');
    }
    setSaving(false);
  };

  return (
    <div
      className="p-5 bg-[#1E1E1E] border border-[#2A2A2A] rounded-lg space-y-3"
      data-testid="project-brief-panel"
    >
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-mono uppercase tracking-wider text-[#F2A900] flex items-center gap-2">
          <FileText className="w-3.5 h-3.5" />
          Project Brief
        </h3>
        <button
          type="button"
          onClick={openEdit}
          data-testid="project-brief-edit"
          className="p-1 text-[#A0A0A0] hover:text-[#F5F1E8]"
          aria-label="Edit project brief"
          title="Edit project brief"
        >
          <Edit2 className="w-3.5 h-3.5" />
        </button>
      </div>

      {brief ? (
        <pre
          data-testid="project-brief-content"
          className="text-xs font-mono text-[#F5F1E8] whitespace-pre-wrap break-words leading-relaxed"
        >
          {brief.content}
        </pre>
      ) : (
        <div
          data-testid="project-brief-empty"
          className="text-xs font-mono text-[#A0A0A0] italic"
        >
          No project brief yet — click the pencil to add one. Project brief auto-flows into every fray inside this project.
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70"
          data-testid="project-brief-modal"
        >
          <div className="w-full max-w-2xl bg-[#1E1E1E] border border-[#2A2A2A] rounded-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold font-['Barlow_Condensed'] uppercase tracking-wider text-[#F5F1E8]">
                Edit Project Brief
              </h3>
              <button
                type="button"
                onClick={closeEdit}
                className="p-1 text-[#A0A0A0] hover:text-[#F5F1E8]"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              data-testid="project-brief-textarea"
              rows={12}
              autoFocus
              placeholder="Describe what this project is iterating on. Auto-flows into every fray inside the project."
              className="w-full px-3 py-2 bg-[#0F0F0F] border border-[#2A2A2A] rounded text-xs font-mono text-[#F5F1E8] focus:border-[#FF4D00] focus:outline-none"
            />
            {error && (
              <div className="text-[10px] font-mono text-[#A61E17]">{error}</div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeEdit}
                data-testid="project-brief-cancel"
                className="px-3 py-1.5 text-xs font-mono text-[#A0A0A0] hover:text-[#F5F1E8]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={save}
                disabled={saving || !draft.trim()}
                data-testid="project-brief-save"
                className="px-4 py-1.5 bg-[#FF4D00] hover:bg-[#FF6A2A] disabled:bg-[#2A2A2A] disabled:text-[#A0A0A0] text-[#0A0A0A] font-mono text-xs uppercase rounded transition-colors"
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
